import React, { useCallback } from "react";
import {
  Button,
  Divider,
  Flex,
  HStack,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";

import colors from "app/colors";
import {
  ADMIN_PATH,
  ALL_THREADS_PATH,
  CHATS_PATH,
  CONTACTS_PATH,
  HOME_PATH,
  REMINDERS_THREADS_PATH,
  SENT_THREADS_PATH,
  STARRED_THREADS_PATH,
  THREADS_PATH,
  UNSEEN_CHATS_COUNT_SEARCH_FILTERS,
  UNSEEN_MESSAGE_THREAD_COUNT_SEARCH_FILTERS,
  ALL_CHATS_PATH,
  TASKS_PATH,
  LOGOUT_PATH,
  UNSEEN_TASKS_SEARCH_FILTERS,
} from "modules/utils/Constants";
import { useAuth } from "modules/providers/auth-provider";
import { useMixpanel } from "modules/providers/mixpanel-provider";
import { MIXPANEL_EVENT_TYPE } from "modules/providers/mixpanel-provider/mixpanel-provider";
import { useTags } from "modules/providers/tags-provider";
import LeftNavTags from "./left-nav-tags";
import LeftNavDistributionGroups from "./left-nav-distribution-groups";
import {
  PersonGearIcon,
  ChatsIcon,
  ContactsIcon,
  GearIcon,
  LogoIcon,
  LogoutIcon,
  ReminderIcon,
  StarIcon,
  SupportIcon,
  HomeIcon,
  ThreadsIcon,
  SendOutlineIcon,
  FileDownloadIcon,
  ThreadIcon,
  InternalAdminIcon,
  OpenMailIcon,
  ChatsOverlappingIcon,
  LogoTextIcon,
  CircleCheckIcon,
} from "modules/icons";
import useCount from "modules/hooks/use-count";
import { useOverlay } from "modules/providers/overlay-provider";
import {
  DRAWER_TYPE,
  MODAL_TYPE,
} from "modules/providers/overlay-provider/overlay-provider";
import { useFlags } from "launchdarkly-react-client-sdk";
import LeftNavCreateThreadButton from "../../layouts/_shared/left-nav-create-thread-button";
import LeftNavCreateChatButton from "../../layouts/_shared/left-nav-create-chat-button";
import LeftNavCreateTaskButton from "../../layouts/_shared/left-nav-create-task-button";
import Link from "../../layouts/_shared/link";
import UserAvatar from "modules/_shared/user-avatar";
import LeftNavSectionHeader from "./left-nav-section-header";
import LeftNavLink from "./left-nav-link";
import LeftNavCallInvites from "./left-nav-call-invites";

const ICON_BOX_SIZE = 4;

export const LeftNav = () => {
  const { me, isAdmin } = useAuth();
  const { openDrawer, openModal } = useOverlay();
  const { track } = useMixpanel();
  const { leftNavActiveTagCategories } = useTags();
  const { userProvisioning: userProvisioningFF } = useFlags();

  const { count: unseenChatsCount } = useCount(
    UNSEEN_CHATS_COUNT_SEARCH_FILTERS,
  );

  const { count: unseenMessageThreadsCount } = useCount(
    UNSEEN_MESSAGE_THREAD_COUNT_SEARCH_FILTERS,
  );

  const { count: unseenTasksCount } = useCount(UNSEEN_TASKS_SEARCH_FILTERS);

  const onLinkClicked = useCallback(
    (type: string) => () => {
      track(MIXPANEL_EVENT_TYPE.LINK, {
        "Link Location": "Left Hand Nav",
        "Link Action": type,
      });
    },
    [track],
  );

  const onLogoutClicked = useCallback(() => {
    track(MIXPANEL_EVENT_TYPE.SIGN_OUT);
    onLinkClicked("Sign Out")();
  }, [onLinkClicked, track]);

  const openSupport = React.useCallback(() => {
    openDrawer({ type: DRAWER_TYPE.SUPPORT });
    onLinkClicked("Support")();
  }, [openDrawer, onLinkClicked]);

  const openInviteModal = React.useCallback(() => {
    openModal({ type: MODAL_TYPE.INVITE });
    onLinkClicked("Invite")();
  }, [openModal, onLinkClicked]);

  const openDownloadsAndExports = React.useCallback(() => {
    openDrawer({ type: DRAWER_TYPE.DOWNLOADS_EXPORTS });
    onLinkClicked("Downloads & Exports")();
  }, [openDrawer, onLinkClicked]);

  const openUserProfile = React.useCallback(() => {
    openDrawer({ type: DRAWER_TYPE.USER_PROFILE, id: me?.id });
    onLinkClicked("My Profile")();
  }, [openDrawer, me?.id, onLinkClicked]);

  if (!me) return null;

  return (
    <Flex
      bg={colors.blackTertiary}
      as="nav"
      overflowY="auto"
      height="full"
      direction="column"
      justify="space-between"
      color={colors.grayPrimary}
      minW={280}
      w={280}
    >
      <Stack spacing={4} pb={8} h="full" pt={4} px={4}>
        <HStack>
          <Link href="/" alignContent="center">
            <HStack>
              <LogoIcon boxSize={6} />
              <LogoTextIcon
                fill={colors.whiteSecondary}
                mb={1}
                w={81}
                h="24px"
              />
            </HStack>
          </Link>
          <Spacer />
        </HStack>
        <HStack w="full">
          <LeftNavCreateChatButton />
          <LeftNavCreateThreadButton />
          <LeftNavCreateTaskButton />
        </HStack>
        <VStack spacing={1} w="full">
          <LeftNavCallInvites />
          <LeftNavLink
            title="Home"
            icon={HomeIcon}
            color={colors.gray30}
            path={HOME_PATH}
            onClick={onLinkClicked("Home")}
          />
          <LeftNavLink
            title="Reminders"
            icon={ReminderIcon}
            color={colors.gray30}
            path={REMINDERS_THREADS_PATH}
            onClick={onLinkClicked("Reminders")}
          />
        </VStack>
        <VStack spacing={1} w="full">
          <LeftNavSectionHeader title="CHATS" color={colors.blue} />
          <LeftNavLink
            title="Chats Inbox"
            icon={ChatsIcon}
            color={colors.blue}
            path={CHATS_PATH}
            onClick={onLinkClicked("Chats")}
            unseenCount={unseenChatsCount}
          />
          <LeftNavLink
            title="All Chats"
            icon={ChatsOverlappingIcon}
            color={colors.blue}
            path={ALL_CHATS_PATH}
            onClick={onLinkClicked("All Chats")}
          />
        </VStack>
        <VStack spacing={1} w="full">
          <LeftNavSectionHeader title="THREADS" color={colors.orangePrimary} />
          <LeftNavLink
            title="Threads Inbox"
            icon={ThreadsIcon}
            color={colors.orangePrimary}
            path={THREADS_PATH}
            onClick={onLinkClicked("Threads Inbox")}
            unseenCount={unseenMessageThreadsCount}
          />
          <LeftNavLink
            title="Starred"
            icon={StarIcon}
            color={colors.orangePrimary}
            path={STARRED_THREADS_PATH}
            onClick={onLinkClicked("Starred Threads")}
          />
          <LeftNavLink
            title="Sent"
            icon={SendOutlineIcon}
            color={colors.orangePrimary}
            path={SENT_THREADS_PATH}
            onClick={onLinkClicked("Sent Threads")}
          />
          <LeftNavLink
            title="All Threads"
            icon={ThreadIcon}
            color={colors.orangePrimary}
            path={ALL_THREADS_PATH}
            onClick={onLinkClicked("All")}
          />
          <Divider borderColor={colors.gray50} />
          <LeftNavDistributionGroups key="left-nav-distribution-groups" />
          {leftNavActiveTagCategories.length > 0 && (
            <LeftNavTags key="left-nav-tags" />
          )}
        </VStack>
        <VStack spacing={1} w="full">
          <LeftNavSectionHeader title="TASKS" color={colors.greenPrimary} />
          <LeftNavLink
            title="Tasks"
            icon={CircleCheckIcon}
            color={colors.greenPrimary}
            path={TASKS_PATH}
            onClick={onLinkClicked("All Tasks")}
            unseenCount={unseenTasksCount}
          />
        </VStack>
        <Divider borderColor={colors.gray50} />
        <VStack spacing={2} w="full">
          <LeftNavLink
            title="Contacts"
            icon={ContactsIcon}
            color={colors.gray30}
            path={CONTACTS_PATH}
            onClick={onLinkClicked("Contacts")}
          />
          <Button
            key="left-nav-support"
            variant="link"
            alignSelf="flex-start"
            py={1}
            pl={4}
            onClick={openSupport}
            fontSize={14}
            color={colors.gray30}
            leftIcon={
              <SupportIcon boxSize={ICON_BOX_SIZE} fill={colors.gray30} />
            }
          >
            Support
          </Button>
          <Button
            key="left-nav-downloads-exports"
            variant="link"
            alignSelf="flex-start"
            py={1}
            pl={4}
            onClick={openDownloadsAndExports}
            fontSize={14}
            color={colors.gray30}
            leftIcon={
              <FileDownloadIcon boxSize={ICON_BOX_SIZE} fill={colors.gray30} />
            }
          >
            Downloads & Exports
          </Button>
          {isAdmin && (
            <LeftNavLink
              title="Admin"
              icon={PersonGearIcon}
              color={colors.grayPrimary}
              path={`${ADMIN_PATH}/users`}
              onClick={onLinkClicked("Admin")}
            />
          )}
          {me.isInternalAdmin && (
            <LeftNavLink
              title="Internal Admin"
              icon={InternalAdminIcon}
              color={colors.grayPrimary}
              path="/internal-admin/organizations"
              onClick={onLinkClicked("Internal Admin")}
            />
          )}
        </VStack>
        <Spacer />
        <VStack spacing={1} w="full">
          {userProvisioningFF && (
            <Button
              key="left-nav-invite"
              variant="link"
              alignSelf="flex-start"
              w="full"
              h="30px"
              onClick={openInviteModal}
              borderWidth={1}
              borderColor={`${colors.grayPrimary}4D`}
              borderRadius={2}
            >
              <HStack w="full" pl={6} pr={2}>
                <OpenMailIcon
                  boxSize={ICON_BOX_SIZE}
                  fill={colors.grayPrimary}
                />
                <Text fontSize={14} color={colors.grayPrimary} fontWeight={500}>
                  Invite Teammates
                </Text>
                <Spacer />
                <Text
                  fontSize={10}
                  color={colors.white}
                  fontWeight={600}
                  justifyContent="center"
                  alignItems="center"
                  borderRadius={2}
                  w="31px"
                  h="16px"
                  bg={colors.gray50}
                  display="flex"
                >
                  New!
                </Text>
              </HStack>
            </Button>
          )}
          <Button
            variant="link"
            w="full"
            backgroundColor={`${colors.black}E6`}
            onClick={openUserProfile}
            fontSize={14}
            h="40px"
          >
            <HStack w="full" px={2}>
              <UserAvatar
                userId={me.id}
                size="xs"
                displayOnlineIndicator
                badgeBorderColor={`${colors.black}E6`}
              />
              <Text noOfLines={1}>My Profile</Text>
              <Spacer />
              <GearIcon boxSize={5} fill={colors.gray30} />
            </HStack>
          </Button>
          <LeftNavLink
            title="Log Out"
            icon={LogoutIcon}
            color={colors.grayPrimary}
            path={LOGOUT_PATH}
            onClick={onLogoutClicked}
          />
        </VStack>
      </Stack>
    </Flex>
  );
};
